/**
 * @graphql cc
 */
import { useMemo } from 'react';

import { useActiveCommentsQuery } from '@confluence/comments-data/entry-points/useActiveCommentsQuery';
import { expValEquals } from '@confluence/feature-experiments';
import { CommentsType } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';

import { useCommentCountsLegacy } from './useCommentCountsLegacy';

/**
 * @deprecated This type is deprecated and will be removed in a future version.
 * It was used to manage comment counts but is no longer needed as part of modernization.
 */
export type UseCommentCountsData = {
	unresolvedInlineCommentsCount: number;
	footerCommentsCount: number;
};

/**
 * @deprecated This hook is deprecated and will be removed in a future version.
 * It was used to fetch comment counts but is no longer needed as part of modernization.
 */
export const useCommentCounts = (contentId: string): UseCommentCountsData => {
	// Always call both hooks to follow React rules, but skip the queries conditionally
	// Legacy implementation using CommentsSectionQuery
	const legacyResult = useCommentCountsLegacy(contentId);

	// New implementation using ActiveCommentsQuery
	// Call without type filter to get both inline and footer comments, then filter by location.type
	const { data: allCommentsData } = useActiveCommentsQuery({
		pageId: contentId,
		// Don't pass type to get all comment types (both CommentsType.INLINE and CommentsType.FOOTER)
		skip: !expValEquals('confluence_comment_use_active_comment_query_exp', 'isEnabled', true) || !contentId,
	});

	return useMemo(() => {
		if (allCommentsData?.comments?.nodes && expValEquals('confluence_comment_use_active_comment_query_exp', 'isEnabled', true)) {
			// New implementation using ActiveCommentsQuery
			// ActiveCommentsQuery already filters by commentState: UNRESOLVED, so all comments are unresolved
			// Filter comments by location.type to count inline vs footer separately
			const unresolvedInlineCommentsCount = allCommentsData.comments.nodes.filter(
				(node) => node?.location?.type === CommentsType.INLINE,
			).length;
			const footerCommentsCount = allCommentsData.comments.nodes.filter(
				(node) => node?.location?.type === CommentsType.FOOTER,
			).length;

			return {
				unresolvedInlineCommentsCount,
				footerCommentsCount,
			};
		}

		// Legacy implementation using CommentsSectionQuery
		// Return legacy result if available, otherwise return default values
		if (legacyResult) {
			return legacyResult;
		}

		// Fallback when experiment is enabled but data hasn't loaded yet
		return {
			unresolvedInlineCommentsCount: 0,
			footerCommentsCount: 0,
		};
	}, [allCommentsData?.comments?.nodes, legacyResult]);
};
